
import { useState, useEffect } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import FilterForm from '@/components/filter-finder/FilterForm';
import FilterRecommendationsSection from '@/components/filter-finder/FilterRecommendationsSection';
import FilterInformationSection from '@/components/filter-finder/FilterInformationSection';
import FilterFaqSection from '@/components/filter-finder/FilterFaqSection';
import FilterCtaSection from '@/components/filter-finder/FilterCtaSection';
import { useToast } from '@/hooks/use-toast';

const FilterFinder = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [vehicleData, setVehicleData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Add useEffect to set isLoaded to true after component mounts
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const getVitanoFilterRecommendations = (data: any) => {
    // Generate specific filter recommendations based on vehicle and filter type
    const make = data.make || '';
    const model = data.model || '';
    const year = parseInt(data.year) || 2020;
    const engineType = data.engineType || '';
    const filterType = data.filterType || 'Air Filter';
    
    // Base recommendations
    let filterRecs = [];
    let filterPrefix = '';
    
    // Set filter prefix based on type (using Vitano conventions)
    switch(filterType) {
      case 'Air Filter':
        filterPrefix = 'VAF';
        break;
      case 'Oil Filter':
        filterPrefix = 'VOF';
        break;
      case 'Fuel Filter':
        filterPrefix = 'VFF';
        break;
      case 'Cabin Air Filter':
        filterPrefix = 'VCF';
        break;
      default:
        filterPrefix = 'VAF';
    }
    
    // Generate model-specific filter numbers and recommendations
    let modelSpecificNumber = '';
    
    // Performance category for premium brands
    const premiumBrands = ['BMW', 'Mercedes-Benz', 'Audi', 'Porsche', 'Lexus'];
    const americanBrands = ['Ford', 'Chevrolet', 'Dodge', 'Jeep', 'Ram'];
    const asianBrands = ['Toyota', 'Honda', 'Hyundai', 'Kia', 'Mazda', 'Nissan'];
    const europeanBrands = ['Skoda', 'Volkswagen', 'Fiat', 'Volvo', 'Peugeot', 'Renault'];
    
    // Generate filter model numbers based on vehicle make
    if (premiumBrands.includes(make)) {
      modelSpecificNumber = `${filterPrefix}-${year % 100}${Math.floor(Math.random() * 900) + 100}P`;
    } else if (americanBrands.includes(make)) {
      modelSpecificNumber = `${filterPrefix}-${Math.floor(Math.random() * 900) + 100}A`;
    } else if (asianBrands.includes(make)) {
      modelSpecificNumber = `${filterPrefix}-${Math.floor(Math.random() * 90) + 10}J`;
    } else if (europeanBrands.includes(make)) {
      modelSpecificNumber = `${filterPrefix}-${Math.floor(Math.random() * 90) + 10}E`;
    } else {
      modelSpecificNumber = `${filterPrefix}-${Math.floor(Math.random() * 999) + 1}U`;
    }
    
    // Filter properties based on vehicle age
    let filterQuality = 'Standard';
    let priceMultiplier = 1.0;
    
    if (year >= 2020) {
      filterQuality = 'Premium';
      priceMultiplier = 1.5;
    } else if (year >= 2015) {
      filterQuality = 'Advanced';
      priceMultiplier = 1.25;
    } else if (year >= 2010) {
      filterQuality = 'Standard';
      priceMultiplier = 1.0;
    } else {
      filterQuality = 'Basic';
      priceMultiplier = 0.85;
    }
    
    // Adjust for premium brands
    if (premiumBrands.includes(make)) {
      priceMultiplier *= 1.3;
    }
    
    // Engine type considerations
    if (engineType.includes('Turbo') || engineType.includes('Twin-Turbo')) {
      priceMultiplier *= 1.2;
    } else if (engineType.includes('Diesel')) {
      priceMultiplier *= 1.15;
    } else if (engineType.includes('Hybrid')) {
      priceMultiplier *= 1.1;
    }
    
    // Generate recommendations
    // Premium Vitano recommendation
    filterRecs.push({
      type: filterType,
      modelNumber: `${modelSpecificNumber}/PRO`,
      price: `$${Math.round(29.99 * priceMultiplier)}.99`,
      description: `Vitano Professional ${filterQuality} ${filterType} designed specifically for ${year} ${make} ${model} with ${engineType} engine`,
      benefits: [
        'German engineered filtration technology',
        'Extended service life up to 15,000 miles',
        'Premium materials for maximum engine protection',
        'Manufactured to exceed OE specifications'
      ],
      image: '/lovable-uploads/90959052-01e0-4de5-a75c-812d72605f31.png'
    });
    
    // Standard Vitano recommendation
    filterRecs.push({
      type: filterType,
      modelNumber: modelSpecificNumber,
      price: `$${Math.round(19.99 * priceMultiplier)}.99`,
      description: `Vitano Standard ${filterQuality} ${filterType} compatible with ${year} ${make} ${model}`,
      benefits: [
        'Meets OE specifications and quality standards',
        'Reliable performance for everyday driving',
        'Cost-effective solution with premium materials',
        'Regular maintenance intervals'
      ],
      image: '/lovable-uploads/90959052-01e0-4de5-a75c-812d72605f31.png'
    });
    
    // Economy Vitano recommendation
    filterRecs.push({
      type: filterType,
      modelNumber: `${modelSpecificNumber.split('/')[0]}/ECO`,
      price: `$${Math.round(14.99 * priceMultiplier)}.99`,
      description: `Vitano Economy ${filterType} suitable for ${year} ${make} ${model}`,
      benefits: [
        'Basic filtration performance',
        'Budget-friendly option with reliable quality',
        'Suitable for standard driving conditions',
        'Easy installation with perfect fit'
      ],
      image: '/lovable-uploads/90959052-01e0-4de5-a75c-812d72605f31.png'
    });
    
    return filterRecs;
  };

  const handleFormSubmit = (data: any) => {
    console.log("Filter form submitted with data:", data);
    setVehicleData(data);
    setFormSubmitted(false); // Reset first
    setIsLoading(true);
    
    // Simulate API call with generated recommendations
    setTimeout(() => {
      setIsLoading(false);
      setFormSubmitted(true);
      toast({
        title: "Filter analysis complete",
        description: `We've found the perfect filters for your ${data.year} ${data.make} ${data.model}`,
      });
      
      // Get Vitano filter recommendations based on vehicle data
      const filterRecs = getVitanoFilterRecommendations(data);
      setRecommendations(filterRecs);
    }, 1500);
  };

  return (
    <div className={`min-h-screen bg-white ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}>
      <Navbar />
      
      {/* Page Header */}
      <div className="pt-24 pb-12 bg-white">
        <div className="page-container">
          <div className="flex flex-col items-start text-autocare-blue">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Find the Right Vitano Filters for Your Vehicle</h1>
            <p className="text-gray-600 max-w-xl">
              Enter your vehicle and filter details below and we'll recommend the perfect Vitano replacement to keep your vehicle running smoothly.
            </p>
          </div>
        </div>
      </div>
      
      {/* Filter Form Section */}
      <section className="section-padding -mt-8">
        <div className="page-container">
          <FilterForm onSubmit={handleFormSubmit} />
        </div>
      </section>
      
      {/* Loading indicator */}
      {isLoading && (
        <section className="section-padding bg-white animate-fade-in">
          <div className="page-container">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 border-4 border-autocare-blue-light border-t-autocare-yellow rounded-full animate-spin mb-4"></div>
              <p className="text-lg font-medium text-autocare-blue">Analyzing your vehicle specifications...</p>
              <p className="text-gray-500 text-sm mt-2">Finding the best Vitano filters</p>
            </div>
          </div>
        </section>
      )}
      
      {/* Recommendations Section */}
      <FilterRecommendationsSection 
        recommendations={recommendations} 
        vehicleData={vehicleData} 
        formSubmitted={formSubmitted} 
      />
      
      {/* Information Section */}
      <FilterInformationSection />
      
      {/* FAQ Section */}
      <FilterFaqSection />
      
      {/* CTA Section */}
      <FilterCtaSection />
      
      <Footer />
    </div>
  );
};

export default FilterFinder;
